import React, { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router';

import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle
} from '@mui/material';

import useStores from 'hooks/use-stores';
import gtagEvent from 'main/components/utils/gtag-event';
import { useStartTrial } from 'queries/license/use-start-trial';

export default function useTrialModal() {
    const history = useHistory();
    const { userStore } = useStores();

    const { mutate, isPending } = useStartTrial({
        onSuccess: () => {
            userStore.loadLicenseList();
            setMessage({
                level: 'success',
                text: 'Hooray! Now you are able to use Maximum features of Vulners.com for nearest 30 days!'
            });
        },
        onError: (error) => {
            setMessage({
                level: 'failure',
                text: error.message || 'Something went wrong'
            });
        }
    });

    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState({
        level: 'pending',
        text: 'Are you sure want to start 30-days Trial Period?'
    });

    const { isAnonymous } = userStore.user;

    const openModal = useCallback(() => setOpen(true), []);
    const closeModal = useCallback(() => setOpen(false), []);

    const onAnchorClick = useCallback(() => {
        if (isAnonymous) {
            history.push('/trial');
        } else {
            openModal();
        }
        gtagEvent('trial_click');
    }, [history, isAnonymous, openModal]);

    const modal = useMemo(
        () => (
            <Dialog open={open} onClose={closeModal}>
                <DialogTitle>{'Starting Trial License'}</DialogTitle>
                <DialogContent>
                    <DialogContentText>{message.text}</DialogContentText>
                </DialogContent>
                {message.level === 'success' ? (
                    <DialogActions>
                        <Button onClick={closeModal} data-testid="close">
                            {'Close'}
                        </Button>
                        <Button
                            href="https://docs.vulners.com/docs/api/"
                            color="primary"
                            target="_blank"
                            data-testid="readDocs"
                        >
                            {'Read Docs'}
                        </Button>
                        <Button href="/scan" color="primary" data-testid="startScan">
                            {'Start Scan'}
                        </Button>
                    </DialogActions>
                ) : (
                    <DialogActions>
                        <Button onClick={closeModal} color="primary" disabled={isPending} data-testid="cancel">
                            {'Cancel'}
                        </Button>
                        <Button
                            onClick={() => mutate(undefined)}
                            color="primary"
                            autoFocus
                            disabled={isPending}
                            data-testid="startTrial"
                        >
                            {isPending && (
                                <Box mr={1}>
                                    <CircularProgress size={24} />
                                </Box>
                            )}
                            {'Start trial'}
                        </Button>
                    </DialogActions>
                )}
            </Dialog>
        ),
        [open, closeModal, message.text, message.level, isPending, mutate]
    );

    return { modal, onAnchorClick };
}
